/* @flow */
import * as React from 'react';
import { Link } from 'react-router-dom';

import StatusIndicator from 'components/StatusIndicator/StatusIndicator';
import {
  createSingleOwnedAsset,
  createMultipleOwnedAsset,
  createMultipleOwnedAssetForUsers,
} from 'permissions/asset';

import createReportView from '../components/ReportView/ReportViewBase';
import type { ViewConfig, DataViewColumn } from '../components/ReportView/types';

import {
  actionsListOwnedMultiple,
  actionsListOwnedRentedMultiple,
  actionsListOwnedSingle,
  actionsListRentedMultiple,
  actionsListRentedSingle,
  actionsListOwnedCaas,
  actionsListOwnedCaasMultiple,
} from './actions';

import styles from '../components/DataTable/DataTable.module.scss';
import moment from 'moment';

const columns: DataViewColumn[] = [
  {
    title: 'general:labels.type',
    columnName: 'asset_type',
    flexBasis: 80,
    disableSort: true,
    component: function AssetTypeCell({ display }) {
      // owned, owned-sales and on-demo -> box icon
      // purchased and rented -> turning page
      const isRented = display === 'rented';
      return (
        <span className="table-type" title={isRented ? 'Supplied by Electro Rent' : 'Owned Asset'}>
          {isRented ? (
            <svg>
              <use xlinkHref="#icon-rental-asset" />
            </svg>
          ) : (
            <svg>
              <use xlinkHref="#icon-owned-asset" />
            </svg>
          )}
        </span>
      );
    },
  },
  {
    title: 'general:assets.abbrs.number',
    columnName: 'asset_number',
    flexBasis: 200,
    component: function AssetNoCell({ display, rowData }) {
      return (
        <Link to={`/assets/${rowData.asset_id}`} title={display}>
          {display}
        </Link>
      );
    },
  },
  {
    title: 'general:manufacturer',
    columnName: 'manufacturer_name',
    flexBasis: 280,
  },
  {
    title: 'general:model',
    columnName: 'model',
    flexBasis: 160,
  },
  {
    title: 'general:description',
    columnName: 'model_description',
    flexBasis: 310,
  },
  {
    title: 'general:assets.labels.options_fitted',
    columnName: 'options_fitted',
    flexBasis: 200,
  },
  {
    title: 'general:assets.abbrs.serial_number',
    columnName: 'er_serial_number',
    flexBasis: 220,
    component: function AssetSerialNumberCell({ display, rowData }) {
      return (
        <Link to={`/assets/${rowData.asset_id}`} title={display}>
          {display}
        </Link>
      );
    },
  },
  {
    title: 'general:calibrations.abbrs.due_in',
    columnName: 'er_calibration_due_date',
    flexBasis: 170,
    component: function AssetCalibrationDueInDays({ display }) {
      const calDueDate = moment(display).diff(moment(), 'days');

      return (
        <span title={calDueDate ? `Calbration due in ${calDueDate} days` : ''}>
          {display === null ? '---' : `${calDueDate} days`}
        </span>
      );
    },
  },
  {
    title: 'general:labels.location',
    columnName: 'locations_name',
    flexBasis: 300,
  },
  {
    title: 'general:labels.assigned_to',
    columnName: 'full_name',
    flexBasis: 200,
  },
  {
    title: 'general:calibrations.labels.type',
    columnName: 'caas_type',
    flexBasis: 160,
    hidden: true,
  },
  {
    title: 'general:calibrations.labels.currency',
    columnName: 'caas_currency',
    flexBasis: 160,
    hidden: true,
  },
  {
    title: 'general:calibrations.labels.price',
    columnName: 'caas_price',
    flexBasis: 160,
    hidden: true,
  },
  {
    title: 'general:calibrations.labels.rental_backfill',
    columnName: 'caas_is_rental_backfill',
    flexBasis: 160,
    hidden: true,
    component: ({ rowData }) => (rowData.caas_is_rental_backfill ? 'Yes' : 'No'),
  },
  {
    title: 'Audit',
    columnName: 'location_confirmed',
    flexBasis: 120,
    disableSort: true,
    component: function AssetAuditCell({ display }) {
      return (
        <span className={styles['table-type']} title={display ? 'Present' : 'Unknown'}>
          <svg className={display ? styles.approvedIcon : styles.disabledIcon}>
            {display ? <use xlinkHref="#icon-approve" /> : <use xlinkHref="#icon-declined" />}
          </svg>
        </span>
      );
    },
  },
  {
    title: 'general:assets.labels.status',
    columnName: 'asset_status',
    flexBasis: 250,
    component: function AssetStatusCell({ display }) {
      return <StatusIndicator status={display} />;
    },
  },
];

const viewConfig: ViewConfig = {
  title: 'general:asset_plural',
  codename: 'assets',
  type: 'SINGLE',
  userFilterNamespace: 'Assets',
  view: {
    columns,
    setColumnsVisibilityBasedOnFilters: (columns, filters) => {
      const columnsWhenCaaSFilterIsOn = [
        'caas_type',
        'caas_price',
        'caas_currency',
        'caas_is_rental_backfill',
      ];
      const columnsWhenCaaSFilterIsOff = [
        'er_calibration_due_date',
        'locations_name',
        'full_name',
        'location_confirmed',
      ];

      const isCaaSFilterOn = filters.some(filter => {
        if (filter.key === 'is_caas_asset') {
          return filter.values.includes('Yes');
        }

        return false;
      });

      columns.forEach(column => {
        if (columnsWhenCaaSFilterIsOn.includes(column.columnName)) {
          column.hidden = !isCaaSFilterOn;
        }

        if (columnsWhenCaaSFilterIsOff.includes(column.columnName)) {
          column.hidden = isCaaSFilterOn;
        }
      });
    },
    idKey: 'asset_id',
    dataPathname: 'as-assets',
    apiType: 'AzureSearch',
    actionsCaas: selectedItems => {
      return selectedItems.length > 1 ? actionsListOwnedCaasMultiple : actionsListOwnedCaas;
    },
    actions: selectedItems => {
      const uniqueSelectedItemTypes = Array.from(
        new Set(
          selectedItems.map(item => {
            return item.asset_type;
          })
        )
      );

      if (uniqueSelectedItemTypes.length > 1) {
        return actionsListOwnedRentedMultiple;
      }

      if (uniqueSelectedItemTypes.length === 1) {
        switch (uniqueSelectedItemTypes[0]) {
          case 'rented':
            return selectedItems.length > 1 ? actionsListRentedMultiple : actionsListRentedSingle;

          case 'owned':
            return selectedItems.length > 1 ? actionsListOwnedMultiple : actionsListOwnedSingle;

          default:
        }
      }

      return [];
    },
  },
  buttons: [
    {
      label: 'general:actions.new',
      icon: '#icon-add',
      subActions: [
        {
          label: 'general:assets.single_asset',
          to: '/assets/create',
          permissions: createSingleOwnedAsset,
        },
        {
          label: 'general:assets.multiple_assets',
          to: '/assets/create-multiple/',
          permissions: createMultipleOwnedAsset,
        },
        {
          label: 'general:assets.multiple_assets',
          to: {
            pathname: '/support',
            state: { id: 'Upload My Asset List' },
          },
          permissions: createMultipleOwnedAssetForUsers,
        },
      ],
    },
  ],
};

export default createReportView(viewConfig);
